// src/SongDetail.js
import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button } from 'react-bootstrap';
import ReviewForm from './components/ReviewForm';
import ReviewList from './components/ReviewList';
import ReadOnlyStars from './components/ReadOnlyStars';


export default function SongDetail({ currentUser }) {
  const { id } = useParams();
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [newReview, setNewReview] = useState(null);
  const [reviewSummary, setReviewSummary] = useState({ avgRating: null, count: 0 });
  
  useEffect(() => {
    if (!id) return;
    fetch(`/api/reviews/summary?type=track&id=${encodeURIComponent(id)}`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(data => setReviewSummary(data))
      .catch(() => setReviewSummary({ avgRating: null, count: 0 }));
  }, [id]);

  useEffect(() => {
    const ac = new AbortController();

    setLoading(true);
    setError(null);
    setTrack(null);

    fetch(`/api/spotify/tracks/${id}`, { signal: ac.signal })
      .then(async (res) => {
        const text = await res.text(); // handle empty bodies
        if (!res.ok) {
          const msg = res.status === 404
            ? 'Track not found'
            : (text || `Failed to fetch track (${res.status})`);
          throw new Error(msg);
        }
        return text ? JSON.parse(text) : null;
      })
      .then((data) => setTrack(data))
      .catch((err) => {
        if (err.name !== 'AbortError') setError(err.message || 'Failed to load track');
      })
      .finally(() => setLoading(false));

    return () => ac.abort();
  }, [id]);

  if (loading) {
    return (
      <Container className="my-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading track...</p>
        </div>
      </Container>
    );
  }

  if (loading || (!track && !error)) {
    return (
      <Container className="my-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading track...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-4">
        <div className="alert alert-danger">
          <h4>Error loading track</h4>
          <p>{error}</p>
        </div>
      </Container>
    );
  }

  const minutes = Math.floor(track.duration_ms / 60000);
  const seconds = String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0');
  const artistName = track.artists?.map(a => a.name).join(', ') || 'Unknown Artist';
  const albumName = track.album?.name || 'Unknown Album';
  const albumImage = track.album?.images?.[0]?.url;

  const handleReviewSubmitted = (reviewData) => {
    setNewReview(reviewData);
    setReviewSubmitted(true);
    // Reset after a short delay
    setTimeout(() => {
      setReviewSubmitted(false);
      setNewReview(null);
    }, 1000);
  };

  return (
    <Container className="my-4">
      <Row className="justify-content-center">
        <Col xs={12}>
          {/* Album + song info row */}
          <div className="song-detail-container">
            {albumImage && (
              <img 
                src={albumImage} 
                alt={`${albumName} cover`} 
                className="song-detail-image" 
              />
            )}

            <div className="song-info">
              <h2 className="song-title">{track.name || 'Unknown Track'}</h2>
              
              <div className="d-flex align-items-center gap-2" style={{ margin: '0.25rem 0 0.75rem' }}>
                {reviewSummary.count > 0 ? (
                  <>
                    <ReadOnlyStars value={reviewSummary.avgRating} ariaLabel={`Average rating ${reviewSummary.avgRating} out of 5`} />
                    <strong style={{ fontSize: '0.95rem' }}>{reviewSummary.avgRating.toFixed(1)}</strong>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>
                      ({reviewSummary.count} review{reviewSummary.count === 1 ? '' : 's'})
                    </span>
                  </>
                ) : (
                  <span style={{ color: '#666', fontSize: '0.9rem' }}>No reviews yet</span>
                )}
              </div>

              <p className="song-meta">
                <Link to={`/album/${track.album.id}`}>{albumName}</Link>
                {' • '}
                {track.artists?.map((artist, index) => (
                  <span key={artist.id}>
                    <Link to={`/artist/${artist.id}`}>{artist.name}</Link>
                    {index < (track.artists?.length ?? 0) - 1 ? ', ' : ''}
                  </span>
                ))}
                {' • '}
                {track.album?.release_date?.slice(0, 4) || 'Unknown'}
              </p>

              <p className="song-meta song-duration">
                {minutes}:{seconds}
              </p>

              <Button
                href={track.external_urls?.spotify}
                target="_blank"
                rel="noopener noreferrer"
                variant="success"
              >
                <i className="bi bi-spotify"></i> Play on Spotify
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Reviews Section */}
      <Row className="mt-4">
        <Col>
          <ReviewForm 
            spotifyId={id} 
            type="track" 
            itemName={track.name}
            itemImage={track.album?.images?.[0]?.url}
            onReviewSubmitted={handleReviewSubmitted}
          />
          <ReviewList 
            spotifyId={id} 
            mode="item"
            onReviewSubmitted={reviewSubmitted}
            showItemInfo={false}
            currentUser={currentUser}
            newReview={newReview}
          />
        </Col>
      </Row>
    </Container>
  );
}
